import { useState, useEffect, useRef } from "react";
import { PageHeader, MetricCard, Badge, formatINR } from "../../../Component/Workshop/UI/index";
import {
  useOverviewKPIs,
  useOverviewMonthly,
  useOverviewStages,
  useOverviewVehicles,
  useWorkshopStats,
  useSurveyorStats,
  useClaimsSummary,
  useSLASummary,
  useDailyReport,
  useReportExportCases,
  useReportSearch,
} from "../../../hooks/useReportQueries";
import DailyReportTab from "./Tabs/DailyReportTab";
import {
  ChartCard,
  BarChart,
  LineChart,
  DonutChart,
  HorizontalBar,
  HorizontalBarFull,
  RatingBar,
  _cellAddr,
  _styleCell,
  _styleRange,
  getFinalStatusEmoji,
} from "./ReportTabUtils";
import {
  _buildDailyStatusSheet,
  _buildMasterTrackerSheet,
  _buildSummaryDashboardSheet,
} from "./ExcelReports";
import ClientsTab from "./Tabs/ClientsTab";

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MONTHLY_CASES = [
  { month:"Aug",  cases:28, settled:18, rejected:3, revenue:2240000 },
  { month:"Sep",  cases:34, settled:24, rejected:2, revenue:2890000 },
  { month:"Oct",  cases:29, settled:20, rejected:4, revenue:2450000 },
  { month:"Nov",  cases:41, settled:31, rejected:3, revenue:3560000 },
  { month:"Dec",  cases:38, settled:28, rejected:5, revenue:3120000 },
  { month:"Jan",  cases:52, settled:36, rejected:4, revenue:4380000 },
];

const STAGE_DISTRIBUTION = [
  { stage:"Case Created",       count:24, color:"#3b82f6" },
  { stage:"Survey Process",     count:18, color:"#a855f7" },
  { stage:"Workshop Repair",    count:31, color:"#f97316" },
  { stage:"Delivery & Payment", count:12, color:"#14b8a6" },
  { stage:"Claim Settlement",   count:9,  color:"#22c55e" },
];

const SURVEYOR_PERF = [
  { name:"Rajesh Kumar",  surveys:145, avgResp:2,   rating:4.6, approved:134, rejected:11 },
  { name:"Priya Mehta",   surveys:89,  avgResp:1.5, rating:4.8, approved:85,  rejected:4  },
  { name:"Suresh Patel",  surveys:212, avgResp:3,   rating:4.3, approved:191, rejected:21 },
  { name:"Amit Patel",    surveys:67,  avgResp:2.5, rating:4.5, approved:62,  rejected:5  },
  { name:"Kiran Desai",   surveys:178, avgResp:4,   rating:4.1, approved:158, rejected:20 },
];

const INSURER_BREAKDOWN = [
  { name:"HDFC Ergo",          claims:34, approved:29, rejected:5,  avgDays:8,  totalPaid:28900000, color:"#3b82f6" },
  { name:"Bajaj Allianz",      claims:28, approved:25, rejected:3,  avgDays:11, totalPaid:21000000, color:"#22c55e" },
  { name:"ICICI Lombard",      claims:21, approved:19, rejected:2,  avgDays:7,  totalPaid:15200000, color:"#a855f7" },
  { name:"New India Assurance",claims:18, approved:14, rejected:4,  avgDays:14, totalPaid:11200000, color:"#f97316" },
  { name:"Oriental Insurance", claims:15, approved:11, rejected:4,  avgDays:16, totalPaid:7800000,  color:"#14b8a6" },
  { name:"United India",       claims:12, approved:10, rejected:2,  avgDays:9,  totalPaid:6300000,  color:"#eab308" },
];

const SLA_TREND = [
  { month:"Aug", breaches:12, total:28, pct:43 },
  { month:"Sep", breaches:9,  total:34, pct:26 },
  { month:"Oct", breaches:11, total:29, pct:38 },
  { month:"Nov", breaches:7,  total:41, pct:17 },
  { month:"Dec", breaches:6,  total:38, pct:16 },
  { month:"Jan", breaches:8,  total:52, pct:15 },
];

const VEHICLE_TYPES = [
  { type:"Car",           count:98,  color:"#3b82f6" },
  { type:"SUV",           count:54,  color:"#a855f7" },
  { type:"Truck",         count:31,  color:"#f97316" },
  { type:"Hatchback",     count:27,  color:"#22c55e" },
  { type:"Heavy Vehicle", count:12,  color:"#eab308" },
];


// ─── Report Tabs ──────────────────────────────────────────────────────────────
const REPORT_TABS = [
  { key:"tci",       label:" Daily Report",  icon:"📋" },
  { key:"overview",  label:"Overview",           icon:"📊" },
  { key:"workshops", label:"Workshop Analytics", icon:"🔧" },
  { key:"surveyors", label:"Surveyor Analytics", icon:"👤" },
  { key:"clients",   label:"Clients",            icon:"🏢" },
  { key:"claims",    label:"Claims & Payments",  icon:"💰" },
  { key:"sla",       label:"SLA Performance",    icon:"⏱️" }

];

const DATE_RANGES = ["Last 7 days", "Last 30 days", "Last 6 months", "Last 12 months", "Custom"];

// ─── Export scope modal ────────────────────────────────────────────────────────
function ExportScopeModal({ onClose, onSelect }) {
  const options = [
    { key: "ACTIVE", label: "Active Cases Only", desc: "Cases still in progress" },
    { key: "CLOSED", label: "Closed Cases Only",  desc: "Settled or closed cases" },
    { key: "ALL",    label: "All Cases",          desc: "Everything, active + closed" },
  ];

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,.5)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        className="wd-card"
        style={{ width: 380, padding: 20 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Export scope</div>
        <div style={{ fontSize: 13, color: "var(--wd-t3)", marginBottom: 16 }}>
          Which cases should this report include?
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {options.map((opt) => (
            <button
              key={opt.key}
              onClick={() => onSelect(opt.key)}
              style={{
                textAlign: "left", padding: "10px 12px", borderRadius: 8,
                border: "1px solid var(--wd-border)", background: "var(--wd-hover)",
                cursor: "pointer",
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 13 }}>{opt.label}</div>
              <div style={{ fontSize: 11, color: "var(--wd-t3)" }}>{opt.desc}</div>
            </button>
          ))}
        </div>
        <button className="wd-btn wd-btn-ghost" style={{ marginTop: 14, width: "100%" }} onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ReportsPage() {
  const [activeTab,  setActiveTab]  = useState("overview");
  const [dateRange,  setDateRange]  = useState("Last 6 months");
  const [exporting,  setExporting]  = useState(false);

  // ── TCI report state ──
  const [reportSearch,    setReportSearch]    = useState("");
  const [reportFilters,   setReportFilters]   = useState(null);   // { clientName, vehicleNumber, customerName }
  const [exportEnabled,   setExportEnabled]   = useState(false);
  const [exportTriggered, setExportTriggered] = useState(false);

   // ── Export scope picker + truncation warning ──
  const [exportScopeOpen, setExportScopeOpen] = useState(false);
  const [exportWarning,   setExportWarning]   = useState(null); 

    const { data: kpis = {
    totalCases: 0, activeCases: 0, settled: 0, rejected: 0,
    totalRevenue: 0, avgCycleDays: 0, slaBreachRate: 0, avgRating: 0,
  }} = useOverviewKPIs(dateRange);

  const overviewKpiCards = [
    { label:"Total Cases",  value:kpis.totalCases,            color:"#3b82f6" },
    { label:"Active",       value:kpis.activeCases,           color:"#f97316" },
    { label:"Settled",      value:kpis.settled,               color:"#22c55e" },
    { label:"Rejected",     value:kpis.rejected,              color:"#ef4444" },
    { label:"Avg Cycle",    value:`${kpis.avgCycleDays}d`,    color:"#a855f7" },
    { label:"SLA Breach %", value:`${kpis.slaBreachRate}%`,   color:"#eab308" },
    { label:"Avg Rating",   value:kpis.avgRating ?? "—",     color:"#3b82f6" },
  ];

   // ── Export cases query ────────────────────────────────────────────────────
  const {
    data: exportData,
    isLoading: exportLoading,
    isFetching: exportFetching,
    isError: exportError,
  } = useReportExportCases(reportFilters || {}, exportEnabled && !!reportFilters);

  // When data arrives after user clicks "Export Excel", trigger download
  useEffect(() => {
    if (!exportTriggered) return;

    if (exportData && !exportFetching) {
      triggerExcelDownload(exportData);
      setExportTriggered(false);
      setExporting(false);
      setExportEnabled(false);
    }

    if (exportError) {
      alert("Failed to generate report. Please try again.");
      setExportTriggered(false);
      setExporting(false);
      setExportEnabled(false);
    }
  }, [exportData, exportFetching, exportTriggered, exportError]);

  const handleExport = (format) => {
    if (format === "excel" || format === "csv") {
      if (!reportFilters) {
        alert("Please search and select a client, vehicle number, or customer from the TCI Daily Report tab first.");
        setActiveTab("tci");
        return;
      }
      setExporting(true);
      setExportEnabled(true);
      setExportTriggered(true);
      return;
    }
    // PDF export — existing logic
    setExporting(true);
    setTimeout(() => setExporting(false), 1200);
    console.log("Export:", format, activeTab, dateRange);
  };
  const handleExportAll = (caseState) => {
    setExportScopeOpen(false);
    setExportWarning(null);
    setReportFilters({ all: true, caseState }); // "ACTIVE" | "CLOSED" | "ALL"
    setExporting(true);
    setExportEnabled(true);
    setExportTriggered(true);
  };

  return (
    <div className="wd-animate" style={{ display:"flex", flexDirection:"column", gap:16 }}>

      {/* ── Header ── */}
      <PageHeader
        title="Reports & Analytics"
        subtitle="Performance insights across cases, workshops, surveyors and claims"
        actions={
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <select className="wd-input wd-select" style={{ width:"auto", minWidth:160 }}
              value={dateRange} onChange={e => setDateRange(e.target.value)}>
              {DATE_RANGES.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            {/* <button className="wd-btn wd-btn-ghost" onClick={() => handleExport("pdf")} disabled={exporting}>
              <PdfIcon /> {exporting ? "Exporting…" : "Export PDF"}
            </button> */}
            <button
              className="wd-btn wd-btn-ghost"
              onClick={() => handleExport("excel")}
              disabled={exporting || exportLoading}
              title={reportFilters ? `Export ${reportFilters.clientName || reportFilters.vehicleNumber || reportFilters.customerName} report` : "Select a client in TCI Daily Report tab first"}
            >
              <XlsxIcon />
              {(exporting || exportFetching) && reportFilters && !reportFilters.all ? "Generating…" : "Export Excel"}
            </button>
             <button
              className="wd-btn wd-btn-primary"
              onClick={() => setExportScopeOpen(true)}
              disabled={exporting || exportLoading}
              title="Export a report covering multiple cases"
            >
              <XlsxIcon />
              {(exporting || exportFetching) && reportFilters?.all ? "Generating…" : "Export All Cases"}
            </button>
          </div>
        }
      />
      {/* ── Truncation warning banner ── */}
      {exportWarning && (
        <div
          className="wd-card"
          style={{
            padding: "10px 14px", borderLeft: "3px solid #eab308",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
          }}
        >
          <span style={{ fontSize: 13, color: "var(--wd-t2)" }}>
            ⚠ Showing the first <strong>{exportWarning.returned.toLocaleString()}</strong> of{" "}
            <strong>{exportWarning.totalMatching.toLocaleString()}</strong> matching cases. Narrow by
            client, date range, or active/closed to see the rest.
          </span>
          <button
            className="wd-btn wd-btn-ghost"
            style={{ fontSize: 12, padding: "4px 10px" }}
            onClick={() => setExportWarning(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {exportScopeOpen && (
        <ExportScopeModal
          onClose={() => setExportScopeOpen(false)}
          onSelect={handleExportAll}
        />
      )}

      {/* ── KPI Row ── */}
      <div
        style={{
          display:"grid",
          gridTemplateColumns:`repeat(${overviewKpiCards.length}, minmax(0, 1fr))`,
          gap:10,
        }}
      >
        {overviewKpiCards.map(({ label, value, color }) => (
          <div key={label} className="wd-card" style={{ padding:"14px 12px", borderLeft:`3px solid ${color}` }}>
            <div style={{ fontSize:10, color:"var(--wd-t3)", textTransform:"uppercase", letterSpacing:".05em", fontWeight:600, marginBottom:5 }}>{label}</div>
            <div style={{ fontSize:20, fontWeight:800, color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* ── Report Tabs ── */}
      <div style={{ display:"flex", borderBottom:"1px solid var(--wd-border)" }}>
        {REPORT_TABS.map(t => (
          <button key={t.key}
            className={`wd-detail-tab ${activeTab === t.key ? "active" : ""}`}
            onClick={() => setActiveTab(t.key)}
            style={{ display:"flex", alignItems:"center", gap:6 }}
          >
            <span>{t.icon}</span> {t.label}
            {t.key === "tci" && reportFilters && (
              <span style={{ marginLeft:4, background:"#3b82f6", color:"#fff", borderRadius:10, fontSize:9, padding:"1px 6px", fontWeight:700 }}>
                {reportFilters.clientName || reportFilters.vehicleNumber || reportFilters.customerName}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Tab Content ── */}
      <div className="wd-animate" key={activeTab}>
          {activeTab === "tci"       && (
          <DailyReportTab
            reportSearch={reportSearch}
            setReportSearch={setReportSearch}
            reportFilters={reportFilters}
            setReportFilters={(f) => { setReportFilters(f); setExportEnabled(false); setExportTriggered(false); }}
            onExportExcel={() => handleExport("excel")}
            exporting={exporting || exportFetching}
          />
        )}
        {activeTab === "overview"  && <OverviewTab  dateRange={dateRange} />}
        {activeTab === "workshops" && <WorkshopsTab dateRange={dateRange} />}
        {activeTab === "surveyors" && <SurveyorsTab dateRange={dateRange} />}
        {activeTab === "clients"   && <ClientsTab />}
        {activeTab === "claims"    && <ClaimsTab    dateRange={dateRange} />}
        {activeTab === "sla"       && <SLATab       dateRange={dateRange} />}
      </div>
    </div>
  );
}

// ─── Excel generation (browser-side using SheetJS) ────────────────────────────
export function triggerExcelDownload(exportData) {
  const XLS = window.XLSX;
  if (!XLS) {
    alert(
      'SheetJS not loaded.\n' +
      'Add to index.html:\n' +
      '<script src="https://cdn.jsdelivr.net/npm/xlsx-js-style@1.2.0/dist/xlsx.bundle.js"></script>'
    );
    return;
  }
 
  const { clientName = "Client", clientShort = "CLIENT", cases = [] } = exportData;
  const date    = new Date();
  const dateStr = date.toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });
  const today   = date.toISOString().slice(0, 10);
 
  // ── Derived stats ──────────────────────────────────────────────────────────
  const total      = cases.length;
  const settled    = cases.filter(c => /settled/i.test(c.finalStatus || c.autoStatus)).length;
  const inProcess  = total - settled;
  const overdue    = cases.filter(c => Number(c.daysLapsed) > 120).length;
  const settlePct  = total > 0 ? Math.round((settled / total) * 100) : 0;
  const avgDays    = total > 0
    ? Math.round(cases.reduce((s, c) => s + (Number(c.daysLapsed) || 0), 0) / total)
    : 0;
  const totalIdvRaw = cases.reduce((s, c) => s + (Number(c.idvValue) || 0), 0);
  const totalIdvStr = totalIdvRaw >= 10_000_000
    ? `₹${(totalIdvRaw / 10_000_000).toFixed(2)}Cr`
    : `₹${(totalIdvRaw / 100_000).toFixed(2)}L`;
  const workshopSet = new Set(cases.map(c => c.workshopName).filter(Boolean));
 
  const wb = XLS.utils.book_new();
 
  // ════════════════════════════════════════════════════════════════════════════
  // SHEET 1 — Daily Status Report
  // ════════════════════════════════════════════════════════════════════════════
  _buildDailyStatusSheet(XLS, wb, {
    clientName, clientShort, cases,
    dateStr, total, settled, inProcess, overdue,
  });
 
  // ════════════════════════════════════════════════════════════════════════════
  // SHEET 2 — Master Tracker (Ops)
  // ════════════════════════════════════════════════════════════════════════════
  _buildMasterTrackerSheet(XLS, wb, { clientName, cases, dateStr, settled });
 
  // ════════════════════════════════════════════════════════════════════════════
  // SHEET 3 — Summary Dashboard
  // ════════════════════════════════════════════════════════════════════════════
  _buildSummaryDashboardSheet(XLS, wb, {
    clientName, cases, dateStr,
    total, settled, inProcess, overdue,
    settlePct, avgDays, totalIdvStr,
    activeWorkshops: workshopSet.size,
  });
 
  // ── Write ──────────────────────────────────────────────────────────────────
  const safeClient = (clientShort || "CLIENT").replace(/[^a-zA-Z0-9_-]/g, "_");
  XLS.writeFile(wb, `${safeClient}_Daily_Status_Report_${today}.xlsx`);
}

// ─── Overview Tab ─────────────────────────────────────────────────────────────
function OverviewTab({ dateRange }) {

  const { data: monthlyData = [], isLoading: monthlyLoading } = useOverviewMonthly(dateRange);
  const { data: stageData   = [], isLoading: stagesLoading  } = useOverviewStages(dateRange);
  const { data: vehicleData = [], isLoading: vehiclesLoading } = useOverviewVehicles(dateRange);
 
  const loading = monthlyLoading || stagesLoading || vehiclesLoading;
  // ──────────────────────────────────────────────────────────────────────────
 
  // Show nothing while loading to avoid charts rendering with empty arrays
  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "var(--wd-t3)" }}>
        Loading...
      </div>
    );
  }
 
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      {/* Row 1: Bar chart + Donut */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 340px", gap:16 }}>
        <ChartCard title="Monthly Cases & Settlements" subtitle={dateRange}>
          <BarChart
            data={monthlyData}
            bars={[
              { key:"cases",   label:"Total Cases",  color:"#3b82f6" },
              { key:"settled", label:"Settled",      color:"#22c55e" },
              { key:"rejected",label:"Rejected",     color:"#ef4444" },
            ]}
            xKey="month"
            height={220}
          />
        </ChartCard>

        <ChartCard title="Stage Distribution" subtitle="Current active cases">
          <DonutChart data={stageData} total={stageData.reduce((s,d)=>s+d.count,0)} valueKey="count" labelKey="stage" />
        </ChartCard>
      </div>

      {/* Row 2: Revenue line + vehicle types */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 300px", gap:16 }}>
        <ChartCard title="Monthly Revenue" subtitle="Claim settlement value (₹)">
          <LineChart
            data={monthlyData}
            dataKey="revenue"
            xKey="month"
            color="#14b8a6"
            height={200}
            formatY={(v) => `₹${(v/100000).toFixed(1)}L`}
          />
        </ChartCard>

        <ChartCard title="Vehicle Type Breakdown">
          <HorizontalBar data={vehicleData} valueKey="count" labelKey="type" />
        </ChartCard>
      </div>

      {/* Row 3: Summary table */}
      <ChartCard title="Monthly Summary Table">
        <div className="wd-table-wrap">
          <table className="wd-table">
            <thead>
              <tr>
                <th>MONTH</th><th>TOTAL CASES</th><th>SETTLED</th><th>REJECTED</th>
                <th>SETTLEMENT RATE</th><th>REVENUE</th>
              </tr>
            </thead>
            <tbody>
              {monthlyData.map(row => (
                <tr key={row.month}>
                  <td style={{ fontWeight:600 }}>{row.month}</td>
                  <td>{row.cases}</td>
                  <td style={{ color:"var(--wd-green)", fontWeight:600 }}>{row.settled}</td>
                  <td style={{ color:"var(--wd-red)" }}>{row.rejected}</td>
                  <td>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <div style={{ flex:1, height:4, borderRadius:2, background:"var(--wd-hover)" }}>
                        <div style={{ height:"100%", borderRadius:2, width:`${Math.round((row.settled/row.cases)*100)}%`, background:"var(--wd-green)" }} />
                      </div>
                      <span style={{ fontSize:12, color:"var(--wd-green)", fontWeight:600, minWidth:32 }}>
                        {row.cases > 0 ? Math.round((row.settled/row.cases)*100) : 0}%
                      </span>
                    </div>
                  </td>
                  <td style={{ fontFamily:"var(--wd-mono)", fontWeight:600 }}>
                    ₹{(row.revenue/100000).toFixed(1)}L
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>
    </div>
  );
}

// ─── Workshops Tab ────────────────────────────────────────────────────────────
function WorkshopsTab({ dateRange }) {
  // const total = WORKSHOP_PERF.reduce((s, w) => s + w.cases, 0);
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, } = useWorkshopStats(dateRange);

  const workshops = data?.pages.flatMap(p => p.data) ?? [];
  const maxCases = Math.max(...workshops.map(w => w.totalCases), 1);

    if (isLoading) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "var(--wd-t3)" }}>
        Loading...
      </div>
    );
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 320px", gap:16 }}>
        {/* Cases per workshop bar */}
        <ChartCard title="Cases by Workshop" subtitle="Total cases handled">
          <HorizontalBarFull
            data={workshops.map(w => ({ label:w.name, value:w.cases, color:"#3b82f6" }))}
            max={maxCases}
          />
        </ChartCard>

        {/* On-time % donut */}
        <ChartCard title="On-Time Delivery Rate" subtitle="% of cases completed on schedule">
          <DonutChart
            data={workshops.map((w,i) => ({
              stage: w.name,
              count: w.onTime,
              color: ["#3b82f6","#22c55e","#f97316","#a855f7","#14b8a6"][i % 5],
            }))}
            total={100}
            valueKey="count"
            labelKey="stage"
            unit="%"
          />
        </ChartCard>
      </div>

      {/* Workshop performance table */}
      <ChartCard title="Workshop Performance Leaderboard">
        <div className="wd-table-wrap">
          <table className="wd-table">
            <thead>
              <tr>
                <th>#</th><th>WORKSHOP</th><th>TOTAL CASES</th>
                <th>AVG DAYS</th><th>ON-TIME %</th><th>RATING</th><th>REVENUE</th>
              </tr>
            </thead>
            <tbody>
              {[...workshops]
                .sort((a, b) => b.cases - a.cases)
                .map((w, i) => (
                <tr key={w.name}>
                  <td>
                    <div style={{
                      width:24, height:24, borderRadius:"50%",
                      background: i === 0 ? "#eab308" : i === 1 ? "#94a3b8" : i === 2 ? "#f97316" : "var(--wd-hover)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:11, fontWeight:800, color: i < 3 ? "#fff" : "var(--wd-t3)",
                    }}>{i+1}</div>
                  </td>
                  <td style={{ fontWeight:600 }}>{w.name}</td>
                  <td>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <div style={{ width:60, height:4, borderRadius:2, background:"var(--wd-hover)" }}>
                        <div style={{ height:"100%", borderRadius:2, width:`${(w.cases/maxCases)*100}%`, background:"var(--wd-blue)" }} />
                      </div>
                      <span style={{ fontWeight:600 }}>{w.cases}</span>
                    </div>
                  </td>
                  <td>
                    <span style={{ color: w.avgDays <= 7 ? "var(--wd-green)" : w.avgDays <= 10 ? "var(--wd-orange)" : "var(--wd-red)", fontWeight:600 }}>
                      {w.avgDays ? `${w.avgDays}d` : "—" }
                    </span>
                  </td>
                  <td>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <div style={{ width:50, height:4, borderRadius:2, background:"var(--wd-hover)" }}>
                        <div style={{ height:"100%", borderRadius:2, width:`${w.onTime}%`, background: w.onTime >= 90 ? "var(--wd-green)" : w.onTime >= 80 ? "var(--wd-orange)" : "var(--wd-red)" }} />
                      </div>
                      <span style={{ fontSize:12, fontWeight:700, color: w.onTime >= 90 ? "var(--wd-green)" : w.onTime >= 80 ? "var(--wd-orange)" : "var(--wd-red)" }}>{w.onTime}%</span>
                    </div>
                  </td>
                  <td>
                    <RatingBar rating={w.rating} />
                  </td>
                  <td style={{ fontFamily:"var(--wd-mono)", fontWeight:600 }}>
                    ₹{(w.revenue/100000).toFixed(1)}L
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {hasNextPage && (
          <div style={{ textAlign:"center", marginTop:12 }}>
            <button
              className="wd-btn wd-btn-ghost"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? "Loading..." : "Load More"}
            </button>
          </div>
        )}
      </ChartCard>
    </div>
  );
}

// ─── Surveyors Tab ────────────────────────────────────────────────────────────
function SurveyorsTab({ dateRange }) {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useSurveyorStats(dateRange)

  const surveyors = data?.pages.flatMap(p => p.data) ?? [];
  const maxSurveys = Math.max(...surveyors.map(s => s.totalSurveys), 1);

   if (isLoading) return <div style={{ padding:40, textAlign:"center", color:"var(--wd-t3)" }}>Loading...</div>;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
        {/* Surveys completed per surveyor */}
        <ChartCard title="Surveys Completed" subtitle="Total across all time">
          <HorizontalBarFull
            data={surveyors.map((s,i) => ({
              label: s.name,
              value: s.surveys,
              color: ["#3b82f6","#a855f7","#22c55e","#f97316","#14b8a6"][i],
            }))}
            max={maxSurveys}
          />
        </ChartCard>

        {/* Approval rate bars */}
        <ChartCard title="Approval vs Rejection Rate">
          <div style={{ display:"flex", flexDirection:"column", gap:14, paddingTop:4 }}>
            {surveyors.map(s => {
              const appPct = s.approvalPct;
              return (
                <div key={s.surveyorCode}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                    <span style={{ fontSize:12, color:"var(--wd-t2)", fontWeight:500 }}>{s.name}</span>
                    <span style={{ fontSize:11, color:"var(--wd-t3)" }}>{appPct}% approved</span>
                  </div>
                  <div style={{ display:"flex", height:8, borderRadius:4, overflow:"hidden" }}>
                    <div style={{ width:`${appPct}%`, background:"var(--wd-green)", transition:"width .4s" }} />
                    <div style={{ flex:1, background:"var(--wd-red)", opacity:.6 }} />
                  </div>
                </div>
              );
            })}
          </div>
        </ChartCard>
      </div>

      {/* Surveyor performance table */}
      <ChartCard title="Surveyor Performance Leaderboard">
        <div className="wd-table-wrap">
          <table className="wd-table">
            <thead>
              <tr>
                <th>#</th><th>SURVEYOR</th><th>TOTAL SURVEYS</th>
                <th>APPROVED</th><th>REJECTED</th><th>APPROVAL %</th>
                <th>AVG RESP</th><th>RATING</th>
              </tr>
            </thead>
            <tbody>
              {[...surveyors]
                .sort((a, b) => b.surveys - a.surveys)
                .map((s, i) => {
                  const appPct = s.approvalPct;
                  return (
                    <tr key={s.name}>
                      <td>
                        <div style={{
                          width:24, height:24, borderRadius:"50%",
                          background: i === 0 ? "#eab308" : i === 1 ? "#94a3b8" : i === 2 ? "#f97316" : "var(--wd-hover)",
                          display:"flex", alignItems:"center", justifyContent:"center",
                          fontSize:11, fontWeight:800, color: i < 3 ? "#fff" : "var(--wd-t3)",
                        }}>{i+1}</div>
                      </td>
                      <td style={{ fontWeight:600 }}>{s.name}</td>
                      <td style={{ fontWeight:600 }}>{s.surveys}</td>
                      <td style={{ color:"var(--wd-green)", fontWeight:600 }}>{s.approved}</td>
                      <td style={{ color:"var(--wd-red)" }}>{s.rejected}</td>
                      <td>
                        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                          <div style={{ width:50, height:4, borderRadius:2, background:"var(--wd-hover)" }}>
                            <div style={{ height:"100%", borderRadius:2, width:`${appPct}%`, background: appPct >= 90 ? "var(--wd-green)" : "var(--wd-orange)" }} />
                          </div>
                          <span style={{ fontSize:12, fontWeight:700, color: appPct >= 90 ? "var(--wd-green)" : "var(--wd-orange)" }}>{appPct}%</span>
                        </div>
                      </td>
                      <td>
                        <span style={{ color: s.avgResp <= 2 ? "var(--wd-green)" : s.avgResp <= 3 ? "var(--wd-orange)" : "var(--wd-red)", fontWeight:600 }}>
                          {s.avgResp ? `${s.avgResp}h` : "—"}
                        </span>
                      </td>
                      <td><RatingBar rating={s.rating} /></td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
         {hasNextPage && (
          <div style={{ textAlign:"center", marginTop:12 }}>
            <button
              className="wd-btn wd-btn-ghost"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? "Loading..." : "Load More"}
            </button>
          </div>
        )}
      </ChartCard>
    </div>
  );
}

// ─── Claims Tab ───────────────────────────────────────────────────────────────
function ClaimsTab({dateRange}) {
  const { data, isLoading } = useClaimsSummary(dateRange);

  if (isLoading) return <div style={{ padding:40, textAlign:"center", color:"var(--wd-t3)" }}>Loading...</div>;

  const {
    summary  = { totalClaims:0, approved:0, rejected:0, totalPaidOut:0 },
    insurers = [],
  } = data ?? {};

  const maxPaid = Math.max(...insurers.map(i => i.totalPaid), 1);

  // const totalClaims  = INSURER_BREAKDOWN.reduce((s, r) => s + r.claims, 0);
  // const totalPaid    = INSURER_BREAKDOWN.reduce((s, r) => s + r.totalPaid, 0);
  // const totalApproved= INSURER_BREAKDOWN.reduce((s, r) => s + r.approved, 0);
  // const totalRejected= INSURER_BREAKDOWN.reduce((s, r) => s + r.rejected, 0);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      {/* Summary KPIs */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
        <MetricCard label="Total Claims"     
        value={summary.totalClaims}  color="#3b82f6" />
        <MetricCard label="Approved"         
        value={summary.approved}color="#22c55e" />
        <MetricCard label="Rejected"         
        value={summary.rejected}color="#ef4444" />
        <div className="wd-card" style={{ padding:16, borderLeft:"3px solid #14b8a6" }}>
          <div style={{ fontSize:11, color:"var(--wd-t3)", textTransform:"uppercase", letterSpacing:".05em", fontWeight:600, marginBottom:6 }}>Total Paid Out</div>
          <div style={{ fontSize:20, fontWeight:800, color:"#14b8a6" }}>
            {summary.totalPaidOut >= 10_000_000
              ? `₹${(summary.totalPaidOut / 10_000_000).toFixed(2)}Cr`
              : `₹${(summary.totalPaidOut / 100_000).toFixed(1)}L`}
          </div>
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 320px", gap:16 }}>
        {/* Revenue bar chart */}
        <ChartCard title="Claims & Payouts by Insurer">
          <div style={{ display:"flex", flexDirection:"column", gap:14, paddingTop:4 }}>
            {insurers.map(ins => (
              <div key={ins.name}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                  <span style={{ fontSize:13, color:"var(--wd-t1)", fontWeight:500 }}>{ins.name}</span>
                  <span style={{ fontSize:12, color:"var(--wd-t2)", fontFamily:"var(--wd-mono)" }}>
                    ₹{(ins.totalPaid/100000).toFixed(1)}L · {ins.claims} claims
                  </span>
                </div>
                <div style={{ height:10, borderRadius:5, background:"var(--wd-hover)", position:"relative", overflow:"hidden" }}>
                  <div style={{ height:"100%", borderRadius:5, width:`${(ins.totalPaid/Math.max(...insurers.map(x=>x.totalPaid)))*100}%`, background:ins.color, transition:"width .5s" }} />
                </div>
                <div style={{ display:"flex", gap:8, marginTop:4 }}>
                  <span style={{ fontSize:10, color:"var(--wd-green)" }}>✓ {ins.approved} approved</span>
                  <span style={{ fontSize:10, color:"var(--wd-red)" }}>✗ {ins.rejected} rejected</span>
                  <span style={{ fontSize:10, color:"var(--wd-t3)" }}>⏱ avg {ins.avgDays}d</span>
                </div>
              </div>
            ))}
          </div>
        </ChartCard>

        {/* Insurer share donut */}
        <ChartCard title="Claim Share by Insurer">
          <DonutChart
            data={insurers.map(i => ({ stage:i.name, count:i.claims, color:i.color }))}
            total={summary.totalClaims}
            valueKey="count"
            labelKey="stage"
          />
        </ChartCard>
      </div>

      {/* Insurer table */}
      <ChartCard title="Insurer Performance Table">
        <div className="wd-table-wrap">
          <table className="wd-table">
            <thead>
              <tr>
                <th>INSURER</th><th>CLAIMS</th><th>APPROVED</th>
                <th>REJECTED</th><th>APPROVAL %</th><th>AVG SETTLEMENT DAYS</th><th>TOTAL PAID</th>
              </tr>
            </thead>
            <tbody>
              {insurers.map(ins => {
                const appPct = ins.claims > 0 ?Math.round((ins.approved / ins.claims) * 100) : 0;
                return (
                  <tr key={ins.name}>
                    <td>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <div style={{ width:10, height:10, borderRadius:"50%", background:ins.color, flexShrink:0 }} />
                        <span style={{ fontWeight:600 }}>{ins.name}</span>
                      </div>
                    </td>
                    <td>{ins.claims}</td>
                    <td style={{ color:"var(--wd-green)", fontWeight:600 }}>{ins.approved}</td>
                    <td style={{ color:"var(--wd-red)" }}>{ins.rejected}</td>
                    <td>
                      <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                        <div style={{ width:50, height:4, borderRadius:2, background:"var(--wd-hover)" }}>
                          <div style={{ height:"100%", borderRadius:2, width:`${appPct}%`, background: appPct >= 80 ? "var(--wd-green)" : "var(--wd-orange)" }} />
                        </div>
                        <span style={{ fontSize:12, fontWeight:700, color: appPct >= 80 ? "var(--wd-green)" : "var(--wd-orange)" }}>{appPct}%</span>
                      </div>
                    </td>
                    <td>
                      <span style={{ color: ins.avgDays <= 9 ? "var(--wd-green)" : ins.avgDays <= 13 ? "var(--wd-orange)" : "var(--wd-red)", fontWeight:600 }}>
                        {ins.avgDays} days
                      </span>
                    </td>
                    <td style={{ fontFamily:"var(--wd-mono)", fontWeight:600 }}>
                      ₹{(ins.totalPaid/100000).toFixed(1)}L
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </ChartCard>
    </div>
  );
}

// ─── SLA Tab ──────────────────────────────────────────────────────────────────
function SLATab({ dateRange }) {
  const { data: res, isLoading } = useSLASummary(dateRange);

  if (isLoading) return <div style={{ padding:40, textAlign:"center", color:"var(--wd-t3)" }}>Loading...</div>;
  
  const monthly = res?.data?.monthly ?? [];
  const stages  = res?.data?.stages  ?? [];

  const latestPct = monthly.length > 0 ? monthly[monthly.length - 1].pct : 0;
  const prevPct   = monthly.length > 1 ? monthly[monthly.length - 2].pct : 0;
  const improved  = latestPct < prevPct;
  const lastMonth = monthly.length > 0 ? monthly[monthly.length - 1] : { breaches:0, total:0 };


  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      {/* SLA KPIs */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
        {[
          { label:"Current Breach Rate",  value:`${latestPct}%`,        color: latestPct < 20 ? "#22c55e" : latestPct < 30 ? "#eab308" : "#ef4444" },
          { label:"MoM Change",           value: monthly.length < 2 ? "—" : improved ? `↓ ${prevPct - latestPct}%` : `↑ ${latestPct - prevPct}%`, color: monthly.length < 2 ? "var(--wd-t3)" : improved ? "#22c55e" : "#ef4444" },
          { label:"Breaches This Month",  value: lastMonth.breaches, color:"#ef4444" },
          { label:"On-Time Cases",        value: lastMonth.total - lastMonth.breaches, color:"#22c55e" },
        ].map(({ label, value, color }) => (
          <div key={label} className="wd-card" style={{ padding:16, borderLeft:`3px solid ${color}` }}>
            <div style={{ fontSize:11, color:"var(--wd-t3)", textTransform:"uppercase", letterSpacing:".05em", fontWeight:600, marginBottom:6 }}>{label}</div>
            <div style={{ fontSize:24, fontWeight:800, color }}>{value}</div>
          </div>
        ))}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
        {/* Breach rate trend line */}
        <ChartCard title="SLA Breach Rate Trend" subtitle="% of cases that breached SLA">
          <LineChart
            data={monthly}
            dataKey="pct"
            xKey="month"
            color="#ef4444"
            height={200}
            formatY={(v) => `${v}%`}
            target={20}
            targetLabel="Target 20%"
          />
        </ChartCard>

        {/* Breach count bar */}
        <ChartCard title="Breaches vs On-Time" subtitle="Monthly comparison">
          <BarChart
            data={monthly.map(m => ({ ...m, onTime: m.total - m.breaches }))}
            bars={[
              { key:"breaches", label:"Breached",  color:"#ef4444" },
              { key:"onTime", label:"On Time", color:"#22c55e" },
            ]}
            xKey="month"
            height={200}
          />
        </ChartCard>
      </div>

      {/* SLA breakdown by stage */}
      <ChartCard title="SLA Breach by Stage" subtitle="Where delays most commonly occur">
        <div style={{ display:"grid", gridTemplateColumns:`repeat(${Math.min(stages.length, 5)},1fr)`, gap:12 }}>
          {stages.map(item => (
            <div key={item.stage} className="wd-card" style={{ padding:16 }}>
              <div style={{ fontSize:11, color:"var(--wd-t2)", fontWeight:600, marginBottom:12, lineHeight:1.4 }}>{item.stage}</div>
              {/* Radial-ish: just a big number with ring */}
              <div style={{ display:"flex", alignItems:"center", justifyContent:"center", marginBottom:12 }}>
                <div style={{ position:"relative", width:70, height:70 }}>
                  <svg width="70" height="70" viewBox="0 0 70 70">
                    <circle cx="35" cy="35" r="28" fill="none" stroke="var(--wd-border-md)" strokeWidth="6" />
                    <circle cx="35" cy="35" r="28" fill="none" stroke={item.color} strokeWidth="6"
                      strokeDasharray={`${2 * Math.PI * 28 * item.breachPct / 100} ${2 * Math.PI * 28}`}
                      strokeLinecap="round"
                      transform="rotate(-90 35 35)"
                    />
                  </svg>
                  <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column" }}>
                    <span style={{ fontSize:16, fontWeight:800, color:item.color }}>{item.breachPct}%</span>
                  </div>
                </div>
              </div>
              <div style={{ textAlign:"center" }}>
                <div style={{ fontSize:10, color:"var(--wd-t3)", textTransform:"uppercase", fontWeight:600 }}>Avg Delay</div>
                <div style={{ fontSize:14, fontWeight:700, color:"var(--wd-t1)", marginTop:2 }}>{item.avgDelay}</div>
              </div>
            </div>
          ))}
        </div>
      </ChartCard>

      {/* SLA monthly table */}
      <ChartCard title="Monthly SLA Detail">
        <div className="wd-table-wrap">
          <table className="wd-table">
            <thead>
              <tr><th>MONTH</th><th>TOTAL CASES</th><th>BREACHES</th><th>ON TIME</th><th>BREACH RATE</th><th>TREND</th></tr>
            </thead>
            <tbody>
              {[...monthly].reverse().map((row, i, arr) => {
                const prev     = arr[i + 1];
                const improved = prev ? row.pct < prev.pct : null;
                return (
                  <tr key={row.month}>
                    <td style={{ fontWeight:600 }}>{row.month}</td>
                    <td>{row.total}</td>
                    <td style={{ color:"var(--wd-red)", fontWeight:600 }}>{row.breaches}</td>
                    <td style={{ color:"var(--wd-green)", fontWeight:600 }}>{row.total - row.breaches}</td>
                    <td>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <div style={{ width:70, height:4, borderRadius:2, background:"var(--wd-hover)" }}>
                          <div style={{ height:"100%", borderRadius:2, width:`${row.pct}%`, background: row.pct < 20 ? "var(--wd-green)" : row.pct < 30 ? "var(--wd-orange)" : "var(--wd-red)" }} />
                        </div>
                        <span style={{ fontSize:12, fontWeight:700, color: row.pct < 20 ? "var(--wd-green)" : row.pct < 30 ? "var(--wd-orange)" : "var(--wd-red)" }}>{row.pct}%</span>
                      </div>
                    </td>
                    <td>
                      {improved === null ? <span style={{ color:"var(--wd-t3)", fontSize:12 }}>—</span>
                        : improved
                        ? <span style={{ color:"var(--wd-green)", fontSize:13, fontWeight:700 }}>↓ Improved</span>
                        : <span style={{ color:"var(--wd-red)", fontSize:13, fontWeight:700 }}>↑ Worsened</span>
                      }
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </ChartCard>
    </div>
  );
}



// ─── Icons ────────────────────────────────────────────────────────────────────
function PdfIcon() { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>; }
function CsvIcon() { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M3 15h18M9 3v18"/></svg>; }
export function XlsxIcon() { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M3 15h18M9 3v18"/><path d="M6 12l3 3-3 3M15 12l-3 3 3 3" strokeLinecap="round"/></svg>; }